import { Dispatch, SetStateAction } from 'react'
import styled from '@emotion/styled'
import Client from 'fhirclient/lib/Client'
import { FcCancel } from "react-icons/fc"
import { HorizontalFlex, IconButton, LabeledItem } from '../components'
import { CdsCard, MedicationAdministration } from '.'
import { toTimeZoneString } from '../utilities'
import { Actions, useAdministrationStateContext } from '../components/state'

const Content = styled.div`
    height: 80%;
`
const Service = styled.div`
    margin: 0.5rem;
    padding: 1rem;
    border-radius: 2px 2px 2px 2px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2), 0 3px 10px 0 rgba(0, 0, 0, 0.19);
`

const Text = styled.div`
    margin-left: 0.5rem;
`

const Description = styled.div`
    margin: 1rem;
`

const DisplayDiv = styled.div`
  overflow-y: scroll;
  height: 100%;
  max-height: 75vh;
  font-size: 0.75vw;
`

const MessageText = styled.div`
  font-size: 14px;
  text-align: center;
`;

export type AdministrationsProps = {
    client: Client
    loading: boolean
    setSelectedAdministrationId: Dispatch<SetStateAction<number | undefined>>
    scheduledAdministrations: MedicationAdministration[]
    forceLatestAdministrationTime: boolean
    handlePopulateMedication: (administration: MedicationAdministration) => void
    setCdsCards: Dispatch<SetStateAction<CdsCard[]>>
    loadAdministrations: () => Promise<MedicationAdministration[]>
    selectedAdministrationId: number | undefined
    setAdministrations: Dispatch<SetStateAction<MedicationAdministration[]>>
}

export function Administrations({
    client,
    loading,
    forceLatestAdministrationTime,
    handlePopulateMedication,
    setCdsCards,
    setSelectedAdministrationId,
    scheduledAdministrations,
    loadAdministrations,
    selectedAdministrationId,
    setAdministrations }: AdministrationsProps) {
    const { globalState: state, dispatch } = useAdministrationStateContext()
    
    const handleAdministrationClick = (administration: MedicationAdministration) => {
        setSelectedAdministrationId(administration.listId)
        handlePopulateMedication(administration)
    }

    async function removeAdministration(e: any, administration: MedicationAdministration) {
        e.stopPropagation()
        setCdsCards([])
        await client.delete(`MedicationAdministration/${administration.id}`)
        await loadAdministrations()
            .then((data: MedicationAdministration[]) => {
                setAdministrations(data)
                setSelectedAdministrationId(undefined)
            })

        dispatch(Actions.SetPrefetchLoaded(false))
    }

    function RenderAdministrations() {
        if (loading) {
            return <DisplayDiv>
                <HorizontalFlex>
                    <div>Loading administrations...</div>
                </HorizontalFlex>
            </DisplayDiv>
        }

        if(state.administrations.length <= 0) {
            return <MessageText>This patient hasn't had any administrations yet</MessageText>
        }

        return <DisplayDiv>
            {scheduledAdministrations?.map((administration, index) => {
                return (administration &&
                    <Service key={index} style={{ backgroundColor: "#D5E5D5" }} onClick={() => handleAdministrationClick(administration)}>
                        <LabeledItem title={'Medication:'}>
                            <Text>{administration.medicationCodeableConcept?.text}</Text>
                        </LabeledItem>
                        <HorizontalFlex>
                            <LabeledItem title={'Administration Time:'}>
                                <Text>{toTimeZoneString(administration.effectiveDateTime)}</Text>
                            </LabeledItem>
                            <LabeledItem title={'Amount:'}>
                                <Text>{administration.dosage.dose.value} {administration.dosage.dose.unit}</Text>
                            </LabeledItem>
                            <LabeledItem title={'Route:'}>
                                <Text>{administration.dosage.route.coding[0].display}</Text>
                            </LabeledItem>
                            <Description />
                            <IconButton onClick={e => removeAdministration(e, administration)}>
                                <HorizontalFlex>
                                    <FcCancel size="1rem"></FcCancel>
                                </HorizontalFlex>
                            </IconButton>
                        </HorizontalFlex>
                    </Service>
                )
            })}
            {state.administrations.map((administration, index) => {
                return (administration &&
                    <Service
                        key={index}
                        style={{
                            backgroundColor:
                                `${forceLatestAdministrationTime && index === 0 && selectedAdministrationId !== undefined
                                    ? "#DBD5E5"
                                    : "white"}`
                        }}
                        onClick={() => handleAdministrationClick(administration)}>
                        <LabeledItem title={'Medication:'}>
                            <Text>{administration.medicationCodeableConcept?.text}</Text>
                        </LabeledItem>
                        <HorizontalFlex>
                            <LabeledItem title={'Administration Time:'}>
                                <Text>{toTimeZoneString(administration.effectiveDateTime)}</Text>
                            </LabeledItem>
                            <LabeledItem title={'Amount:'}>
                                <Text>{administration.dosage.dose?.value} {administration.dosage.dose?.unit}</Text>
                            </LabeledItem>
                            <LabeledItem title={'Route:'}>
                                <Text>{administration.dosage.route.coding[0].display}</Text>
                            </LabeledItem>
                            <Description />
                            <IconButton onClick={e => removeAdministration(e, administration)}>
                                <HorizontalFlex>
                                    <FcCancel size="1rem"></FcCancel>
                                </HorizontalFlex>
                            </IconButton>
                        </HorizontalFlex>
                    </Service>
                )
            })}
        </DisplayDiv>
    }

    return <Content>
        {RenderAdministrations()}
    </Content>
}